import * as Yup from "yup";
import Submit from "@/lib/form/formik/components/buttons/Submit";
import { validationMessages } from "@/lib/form/formik/validationSchema";
import { defaultStyles } from "@/lib/form/formik/components/controls/AutocompleteInput.js";
import { callApi } from "@/lib/apiClient/api";
import {
  PIKSEL,
  STREAMING_FAITH,
  ENDAVO,
  IMPACT_NETWORK,
  BLUE_PIKSEL
} from "@/theme";
import StreamingFaithLogo from "@/images/streamingfaith/streaming_faith_logo_login.png";
import ImpactNetworkLogo from "@/images/impactnetwork/impact_network_logo_login.png";
import PikselLogo from "@/images/piksel/piksel_logo_login.png";
import BluePikselLogo from "@/images/bluepiksel/blue_piksel_logo_login.png";
import EndavoLogo from "@/images/endavo_logo_login.png";
import Cookie from "@/lib/apiClient/cookie";
import { buildSubmitParams } from "./service";

export const registerFormConfig = {
  formName: "Trial Signup",
  className: "mx-auto d-block",
  showStatus: true,
  handleSubmitting: true,
  notEmptySubmit: ["email", "password"],
  fields: [
    {
      name: "email",
      type: "email",
      label: "Email",
      validationSchema: "requiredEmail",
      isRequired: true
    },
    {
      name: "password",
      type: "password",
      label: "Password",
      showStrengthIndicator: true,
      withPasswordReveal: true,
      validationSchema: "password",
      isRequired: true
    },
    {
      name: "passwordScore",
      isVisible: () => false
    },
    {
      name: "firstName",
      type: "text",
      label: "First Name",
      validationSchema: "required",
      isRequired: true
    },
    {
      name: "lastName",
      type: "text",
      label: "Last Name",
      validationSchema: "required",
      isRequired: true
    },
    {
      name: "name",
      type: "text",
      label: "Company or Channel Name",
      helpInputText:
        "Used for your OTT Channel URL. Please limit to 60 characters.",
      isRequired: true,
      definedValidation: Yup.string()
        .required(validationMessages.required)
        .max(60, `This value should be max 60 characters long.`)
    },
    {
      name: "timezone",
      type: "timezone",
      label: "Closest City",
      helpInputText:
        "Used to determine account time zone for live events, schedules, etc.",
      isRequired: true,
      validationSchema: "required",
      autocompleteInputConfig: {
        customStyles: {
          control: (base, state) => ({
            ...base,
            ...defaultStyles.control(base, state),
            height: 48,
            minHeight: 48,
            font: "normal normal normal 15px/15px Lato",
            border: "none",
            borderRadius: "0px",
            backgroundColor: "#f3f3f3"
          }),
          placeholder: (base) => ({
            ...base,
            color: "#808080"
          }),
          valueContainer: (base) => ({
            ...base,
            padding: "0px 18px"
          }),
          singleValue: (base) => ({
            ...base,
            display: "flex",
            flexDirection: "column",
            color: "#495057",
            fontSize: 16
          }),
          input: (base) => ({
            ...base,
            padding: 0
          })
        },
        menuPlacement: "top"
      }
    }
  ],
  buttonsClassName: "col",
  buttons: [
    {
      title: "Create my OTT Channel Now",
      Button: Submit,
      variant: "signup",
      disabled: process.env.REACT_APP_TRIAL_REGISTRATION_SUSPENDED === "true"
    }
  ],
  footerLoadingMessage:
    "It may take a few seconds to create your account. Please stand by!"
};

export const registerNewAccount = async ({ params, onFail }) => {
  return await callApi({
    url: "/communities",
    method: "POST",
    params,
    onFail
  });
};

export const GROUP_CONFIGS = {
  [ENDAVO]: {
    groupConfigName: ENDAVO,
    logo: EndavoLogo,
    fullCompanyName: "Endavo Media and Communications, Inc.",
    name: "Endavo",
    domain: "endavomedia.com",
    marketingWebsite: "www.endavomedia.com",
    cobraDomain: "ottchannel.com",
    email: "info@endavomedia.com",
    contactUsLink: "https://www.endavomedia.com/contact-us/"
  },
  [STREAMING_FAITH]: {
    groupConfigName: STREAMING_FAITH,
    logo: StreamingFaithLogo,
    fullCompanyName: "Streaming Faith",
    name: "Streaming Faith",
    domain: "streamingfaith.com",
    marketingWebsite: "everywhere.streamingfaith.com",
    cobraDomain: "streamingfaith.com",
    email: "info@endavomedia.com",
    contactUsLink: "https://everywhere.streamingfaith.com/contact"
  },
  [PIKSEL]: {
    groupConfigName: PIKSEL,
    logo: PikselLogo,
    fullCompanyName: "Piksel",
    name: "Piksel",
    domain: "piksel.plus",
    marketingWebsite: "piksel.tech",
    cobraDomain: "piksel.plus",
    email: "info@endavomedia.com",
    contactUsLink: "https://calendly.com/piksel"
  },
  [IMPACT_NETWORK]: {
    groupConfigName: IMPACT_NETWORK,
    logo: ImpactNetworkLogo,
    fullCompanyName: "Impact Broadcasting Service",
    name: "Impact Network",
    domain: "watchimpactgo.com",
    marketingWebsite: "watchimpact.com",
    cobraDomain: "watchimpactgo.com",
    email: "info@endavomedia.com",
    contactUsLink: "https://watchimpact.com/"
  },
  [BLUE_PIKSEL]: {
    groupConfigName: BLUE_PIKSEL,
    logo: BluePikselLogo,
    fullCompanyName: "Blue Piksel",
    name: "Blue Piksel",
    domain: "bluepiksel.com",
    marketingWebsite: "piksel.tech",
    cobraDomain: "bluepiksel.com",
    email: "info@endavomedia.com",
    contactUsLink: "https://calendly.com/piksel"
  }
};

export const getGroupConfig = (groupName) =>
  GROUP_CONFIGS[groupName] || GROUP_CONFIGS.default;

export const onSubmit = async (
  submittedParams,
  onFail,
  recaptchaRef,
  groupId,
  signUpMutation
) => {
  recaptchaRef.current.reset();
  let recaptchaToken = null;
  try {
    recaptchaToken = await recaptchaRef.current.executeAsync();
  } catch (e) {
    onFail();
  }

  const hubSpotHutk = Cookie.getItem("hubspotutk");

  const params = buildSubmitParams({
    ...submittedParams,
    groupId,
    recaptchaToken,
    hubSpotHutk
  });

  if (recaptchaToken) {
    signUpMutation.mutate({ params, onFail });
  } else {
    // if no token show general error
    onFail();
  }
};
